import { getVehicleModels } from '../../api/car';

export const CAR_MODEL: string = 'carModel';

/**
 * Actions to be connected from redux.
 * @type {Object}
 */
export const actionsToConnect = {};

/**
 * Values to be connected from redux.
 * @param  {object} state   Redux state
 * @type {Array}
 */
export const propsToConnect = () => {
  return {};
};

/**
 * Sets a suggestion in the form.
 * @param  {Object}   suggestion Object representing a suggestion.
 * @param  {Function} setValues  Set value function of the Formik component.
 * @param  {Array}    values     The current Formik values.
 */
const setFormValues = (suggestion, setValues, values) => {
  const payload = {
    ...values,
    ...suggestion,
  };
  setValues(payload);
};

/**
 * Clears the form.
 * @param  {Function} setValues  Set value function of the Formik component.
 * @param  {Array}    values     The current Formik values.
 */
const clearFormValues = (setValues, values) => {
  const payload = {
    ...values,
    vehicleMakeId: null,
    model: null,
    gpsRequired: null,
  };
  setValues(payload);
};

/**
 * Sets the value of the field.
 * @param  {Object} suggestion Object representing a suggestion.
 * @param  {Object} that       This object from caller.
 */
export const setValue = (suggestion, that) => {
  if (!that.state.isTyping) {
    that.setState({
      value: suggestion.model,
    });
  }
};

/**
 * Function that fetches the value to render.
 * @param  {Object} values The current Formik values.
 * @param  {Object} that   This object from caller.
 * @return {string}        The value to show.
 */
export const getValue = (values, that) => {
  return that.state.value;
};

/**
 * How a suggestion looks when selected.
 * @param  {Object}   suggestion Object representing a suggestion.
 * @param  {Function} setValues  Set value function of the Formik component.
 * @param  {Array}    values     The current Formik values.
 * @return {string}              String representation of the suggestion.
 */
export const getSuggestionValue = (suggestion, setValues, values) => {
  setFormValues(suggestion, setValues, values);
  return suggestion.model;
};

/**
 * How should the suggestions be rendered in the list.
 * @param  {Object} suggestion Object representing a suggestion.
 * @return {string}            String representation of the suggestion.
 */
export const renderSuggestion = suggestion => (
  suggestion.model
);

/**
 * Event to be called on change.
 * @param  {InputEvent} event     The event.
 * @param  {string}     newValue  The current value of the input field.
 * @param  {Object}     that      The "this" object of the caller.
 */
export const onChangeEvent = (event, newValue, that) => {
  that.setState({
    isTyping: true,
    value: newValue,
  });
};

/**
 * Event to call onBlur.
 * @param  {Object}   that      The "this" object of the caller.
 * @param  {Object}   suggestion The suggestion.
 * @param  {Function} setValues  The set values function of the Formik component.
 * @param  {Object}   values     The current values of the Formik componenet.
 */
export const onBlurEvent = (that, suggestion, setValues, values) => {
  if (!suggestion) {
    return;
  }
  setFormValues(suggestion, setValues, values);
  setValue(suggestion, that);
};

const getSuggestionsFromService = (that, params, value) => {
  // If we dont have any suggestions we fetch some.
  if (!that.state.hasSuggestions) {
    return getVehicleModels(params, that.props.dispatch).then(suggestions => {
      that.setState({
        hasSuggestions: true,
        models: suggestions,
      });
      return suggestions;
    });
  }

  if (!value) {
    return [];
  }

  // If we have suggestions we filter in them based on inputted value.
  const { models } = that.state.autoProps;
  const inputValue = value.trim()
    .toLowerCase();
  const inputLength = inputValue.length;
  const suggestion = models.filter(model =>
    model.model.toLowerCase().slice(0, inputLength) === inputValue
  );
  return Promise.resolve(suggestion);
};

/**
 * onSuggestionsFetchRequested is called whenever new suggestions should be fetched.
 * @param  {string}   value      The value entered into the input field.
 * @param  {Object}   that       The "this" object of the caller.
 * @param  {function} dispatch   The dispatch function of the store.
 * @param  {Function} setValues  Set values function of the Formik component.
 * @param  {Array}    values     The current Formik values.
 */
export const onSuggestionsFetchRequested = (value, that, dispatch, setValues, values) => {
  const { make, modelYear } = that.props.values;
  if (!make || !modelYear || value.length === 0) {
    that.setState({
      suggestions: [],
    });
    return;
  }
  that.setState({
    isLoading: true,
  });


  // Only do a new request if params has been updated.
  if (that.state.usedValues) {
    if (that.state.usedValues.make === make && that.state.usedValues.modelYear === modelYear) {
      const inputValue = value.trim().toLowerCase();
      const inputLength = inputValue.length;
      const suggestions = that.state.models.filter(model =>
        model.model && model.model.toLowerCase().slice(0, inputLength) === inputValue
      );

      if (suggestions.length === 1) {
        const suggestion = suggestions[0];
        setFormValues(suggestion, setValues, values);
        setValue(suggestion, that);
      } else if (suggestions.length === 0) {
        clearFormValues(setValues, values);
      }

      that.setState({
        suggestions,
        isLoading: false,
      });
      return;
    }
  }

  const params = {
    make,
    modelYear,
  };
  that.setState({
    loadingClass: 'isLoading',
    loading: true,
  });
  getSuggestionsFromService(that, params, value).then(suggestions => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    const filterSuggestions = suggestions.filter(
      model => model.model && model.model.toLowerCase().slice(0, inputLength) === inputValue
    );
    if (filterSuggestions.length === 1) {
      const suggestion = filterSuggestions[0];
      setFormValues(suggestion, setValues, values);
      setValue(suggestion, that);
    } else if (filterSuggestions.length === 0) {
      clearFormValues(setValues, values);
    }
    that.setState({
      loadingClass: 'loaded',
      loading: false,
      isLoading: false,
      suggestions: filterSuggestions,
      usedValues: {
        make,
        modelYear,
      },
    });
  });
};

/**
 * onSuggestionsClearRequested is called whenever the suggestions should be cleared.
 * @param  {Object} that  The "this" object of the caller.
 * @param  {Function} setValues  Set values function of the Formik component.
 * @param  {Array}    values     The current Formik values.
 */
export const onSuggestionsClearRequested = (that, setValues, values) => {
  if (that.state.value.length === 0) {
    clearFormValues(setValues, values);
  }

  that.setState({
    hasSuggestions: false,
    loadingClass: null,
    suggestions: [],
  });
};

/**
 * Should the suggestions be rendered on focus?
 * @return {boolean} True if suggestions should be rendered initially.
 */
export const shouldRenderSuggestions = () => {
  return true;
};

export const getClasses = that => {
  return [
    that.state.loadingClass,
  ];
};

export const type = CAR_MODEL;



// WEBPACK FOOTER //
// ./src/utilities/form/autoSuggest/carModel.js